/**
 * "Preview in Companion" support: the portal's admin user list opens the
 * Companion with #companion-preview and hands over its access token plus a
 * target user id via postMessage. The API's superuser-gated
 * X-Impersonate-User-Id header does the rest — every endpoint answers as the
 * target user, so the app renders exactly what they would see.
 *
 * The session lives in sessionStorage (per-tab, gone on close) and never
 * touches the normal login's localStorage tokens.
 */

const PREVIEW_HASH = '#companion-preview';
const SESSION_KEY = 'zeo-companion-preview';

export interface PreviewSession {
  access: string;
  impersonateUserId: string;
}

export function getPreviewSession(): PreviewSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as PreviewSession) : null;
  } catch {
    return null;
  }
}

export function endPreview(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

/**
 * Complete the handshake with the opener before the app renders. Resolves
 * immediately when not opened as a preview; falls through to the normal app
 * (login screen) if the opener never answers.
 */
export function initPreview(): Promise<void> {
  if (window.location.hash !== PREVIEW_HASH) return Promise.resolve();
  history.replaceState(null, '', window.location.pathname);
  const opener = window.opener as Window | null;
  if (!opener) return Promise.resolve();

  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
      resolve();
    };
    const timeout = setTimeout(finish, 5000);
    const onMessage = (e: MessageEvent) => {
      if (e.source !== opener) return;
      const d = e.data as Partial<PreviewSession> & { type?: string };
      if (
        d?.type !== 'companion-preview-auth' ||
        typeof d.access !== 'string' ||
        typeof d.impersonateUserId !== 'string'
      ) {
        return;
      }
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ access: d.access, impersonateUserId: d.impersonateUserId })
      );
      finish();
    };
    window.addEventListener('message', onMessage);
    // Carries no secrets; the opener validates our origin before answering.
    opener.postMessage({ type: 'companion-preview-ready' }, '*');
  });
}
